import { Feature, FeatureRenderOptions, MarkerUrls } from '../feature.model';
import { MapDrawingContext } from '../../models/map-drawing-context.model';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { GoogleMapFeatures } from '../../models/layer-map-features.model';
import { LayerColorsService } from 'src/app/modules/cop/drawing/layer-colors.service';
import { FeatureRenderingUtilityService } from 'src/app/modules/cop/drawing/features/feature-rendering-utility.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { cyrb53 } from 'src/app/modules/core/services/hasher';
import { LayerColors } from '../../models/layer-colors.model';
import { GeometryTypes } from '../../models/layer-renderer.model';
import { DatasetZIndexValues } from 'src/app/modules/cop/drawing/models/dataset-zIndex.model';
import { FeatureEvent } from 'src/app/modules/cop/drawing/features/feature.model';
import { MapLabelService } from '../../label.service';
import { TravelerService } from '../../traveler.service';
import { v4 as uuidv4 } from 'uuid';
import { MapLayerQueryBuildService } from '../../../services/data/map-layer-query-build.service';
import { MapLayerClusterService } from '../../cluster.service';
import { API_BASE_URL } from '@app/core/constants';

export const SELECTED_Z_INDEX = 10000;
const ONE_DAY_MILLISEONCDS = 86400000;

export class TravelerFeature implements Feature {
  private googleMapFeatures: GoogleMapFeatures;
  private isVisible: boolean = false;
  private isSelected: boolean = false;
  private markerUrls: MarkerUrls = { normal: '', selected: '' };
  private layerColors: LayerColors;
  private zIndex: number = 0;
  private mouseoutTimers = {};
  private layerUId: string;
  private datasetUId: string;
  private documentUId: string;
  private label?: string;
  private minLabelZoomLevel: number;
  private labelService: MapLabelService;
  private nextRefreshTime: number;
  private featurePollingTimeout: string | number | NodeJS.Timeout;
  private context: MapDrawingContext;
  private options: FeatureRenderOptions;
  private layerHasLabels: boolean;
  private trackIsDrawn: boolean;
  private dataset: Dataset;
  private documentHash: number = 0;
  private clusterService: MapLayerClusterService;
  private timerId: string | number | NodeJS.Timeout;

  constructor() {}

  render(context: MapDrawingContext, options: FeatureRenderOptions): void {
    const { layer, document } = options;

    this.context = context;
    this.options = options;

    this.googleMapFeatures = context.mapFeatures[layer.uId][document.uid];

    const dataset = context.mapState.datasetsSet[layer.datasetUId];

    this.dataset = dataset;
    this.layerUId = layer.uId;
    this.datasetUId = dataset.uId;
    this.documentUId = document.uid;

    this.isSelected = !!context.mapState.selectedDocuments[document.uid];
    this.isVisible = context.isDocumentVisible(layer, document);

    this.label = MapLayerQueryBuildService.getDatasetLabelFieldDisplay(document, context.labelFields);
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;
    this.labelService = context.labelService;
    this.clusterService = context.clusterService;
    this.minLabelZoomLevel = !!layer.displayOptions?.minLabelZoom ? layer.displayOptions?.minLabelZoom : 12;

    let workflowColor: string = null;
    if (context.isWorkflowLayerSection) {
      workflowColor = FeatureRenderingUtilityService.getDocumentWorkflowColor(dataset, document);
    }

    const fillColor = workflowColor ?? LayerColorsService.getLayerFillColor(document.fill_color ?? layer.color);
    const borderColor = LayerColorsService.getLayerBorderColorFromFillColor(fillColor);
    this.layerColors = {
      fillColor,
      borderColor
    };

    const cacheBuster = !!layer?.updatedOn ? new Date(layer.updatedOn).getTime() : context.cacheBuster;
    this.markerUrls = this.buildMarkerImageUrls(layer, dataset, document, cacheBuster);
    this.zIndex = this.isSelected ? SELECTED_Z_INDEX : DatasetZIndexValues[dataset.type][dataset.subtype];

    // build and check the document hash...if the values have not changed then
    // bail out on the rendering process.
    const newHash = this.buildDocumentHash(dataset, layer, document, this.isSelected, this.label);
    if (this.documentHash === newHash) {
      return;
    }
    this.documentHash = newHash;

    this.destroy();

    // get the geometry features from the standard geo field in the document
    // and then render each to the map
    this.addGoogleMapObjects(context, options, layer, dataset, document);

    if (this.isSelected) {
      this.drawTrack(document, context.googleMap);
    }
  }

  setVisible(): void {
    this.isVisible = true;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(true);
      if (!!marker['__data']?.shouldCluster) {
        this.clusterService.registerMarkerAdd(this.layerUId, marker, this.documentUId);
      }
    }
    if (this.layerHasLabels === true) {
      this.showLabel();
    }
    for (let poly of this.googleMapFeatures.polygons) {
      poly.setVisible(true);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(true);
    }
    for (let circ of this.googleMapFeatures.circles) {
      circ.setVisible(true);
    }
    for (let rect of this.googleMapFeatures.rectangles) {
      rect.setVisible(true);
    }

    // recalculate timers
    const allSegmentGeos = TravelerService.getSegmentsForDisplay(document);
    if (!allSegmentGeos) {
      return;
    }

    var allSegmentProperties = allSegmentGeos.map(geo => TravelerService.geoPropertiesToSegmentPropertiesObject(geo?.properties));

    for (let geo of allSegmentGeos) {
      const [visibleNow, visibleWhen] = TravelerService.calculateTravelerFeatureVisibility(
        allSegmentProperties,
        TravelerService.geoPropertiesToSegmentPropertiesObject(geo.properties)
      );
      this.nextRefreshTime = !this.nextRefreshTime ? visibleWhen : Math.min(this.nextRefreshTime, visibleWhen);
    }

    if (!!this.nextRefreshTime && !!this.isVisible && this.nextRefreshTime !== 0) {
      const now = new Date().getTime();
      var refreshLayerDelay = this.nextRefreshTime - now;

      if (refreshLayerDelay > 0) {
        if (refreshLayerDelay > ONE_DAY_MILLISEONCDS) {
          refreshLayerDelay = ONE_DAY_MILLISEONCDS;
        }
        this.startPolling(this.context, this.options, refreshLayerDelay);
      }
    }
  }

  setHidden(): void {
    this.isVisible = false;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(false);
      const labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
      if (labelOverlay) {
        labelOverlay.setVisible(false);
      }
      this.clusterService.registerMarkerRemove(this.layerUId, marker, this.documentUId);
    }
    for (let poly of this.googleMapFeatures.polygons) {
      poly.setVisible(false);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(false);
    }
    for (let circ of this.googleMapFeatures.circles) {
      circ.setVisible(false);
    }
    for (let rect of this.googleMapFeatures.rectangles) {
      rect.setVisible(false);
    }

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    if (!!this.featurePollingTimeout) {
      clearTimeout(this.featurePollingTimeout);
      this.featurePollingTimeout = null;
    }
  }

  setSelected() {
    this.isSelected = true;

    // remove markers and recalculate to show all segments
    const { layer, document } = this.options;
    const dataset = this.context.mapState.datasetsSet[layer.datasetUId];

    this.context.clusterService.clearChanges(this.layerUId, this.documentUId);
    this.destroy();
    this.addGoogleMapObjects(this.context, this.options, layer, dataset, document);
    this.context.clusterService.applyChanges(this.layerUId, this.documentUId);

    this.context.mapFeatures[this.layerUId][this.documentUId] = this.googleMapFeatures;

    this.setMarkerSelected();

    this.drawTrack(document, this.context.googleMap);
  }

  setUnselected() {
    this.isSelected = false;

    const { layer, document } = this.options;
    const dataset = this.context.mapState.datasetsSet[layer.datasetUId];

    this.context.clusterService.clearChanges(this.layerUId, this.documentUId);
    this.destroy();
    this.trackIsDrawn = false;
    this.addGoogleMapObjects(this.context, this.options, layer, dataset, document);
    this.context.clusterService.applyChanges(this.layerUId, this.documentUId);

    this.context.mapFeatures[this.layerUId][this.documentUId] = this.googleMapFeatures;

    // get the geometry features from the standard geo field in the document
    // and then render each to the map
    this.setMarkerNormal();
  }

  showLabel(): void {
    this.layerHasLabels = true;
    if (!!!this.label) {
      return;
    }

    for (let marker of this.googleMapFeatures.markers) {
      let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);

      if (!labelOverlay) {
        labelOverlay = this.labelService.createLabel(
          marker,
          this.label,
          this.layerUId,
          this.documentUId,
          {
            left: null,
            top: -84
          },
          'map-marker-label',
          10000
        );
        this.labelService.addMarkerLabel(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      }
    }
  }

  hideLabel(): void {
    this.layerHasLabels = false;
    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
    }
  }

  private updateLabel(layer: Layer, marker: any): void {
    const hasLabel = !!this.label && layer.displayOptions?.labelsVisible === true;
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
    if (hasLabel) {
      if (labelOverlay) {
        labelOverlay.setLabel(this.label);
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.showLabel();
      }
    } else {
      this.hideLabel();
    }
  }

  destroy(): void {
    this.trackIsDrawn = false;

    for (let circle of this.googleMapFeatures.circles) {
      circle.unbindAll();
      circle.setMap(null);
    }
    this.googleMapFeatures.circles = [];

    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
      this.clusterService.registerMarkerRemove(this.layerUId, marker, this.documentUId);

      google.maps.event.clearInstanceListeners(marker);
      marker.unbindAll();
      marker.setMap(null);
      marker = null;
    }
    this.googleMapFeatures.markers = [];

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.unbindAll();
      polygon.setMap(null);
    }
    this.googleMapFeatures.polygons = [];

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.unbindAll();
      polyline.setMap(null);
    }
    this.googleMapFeatures.polylines = [];

    for (let rectangle of this.googleMapFeatures.rectangles) {
      rectangle.unbindAll();
      rectangle.setMap(null);
    }
    this.googleMapFeatures.rectangles = [];

    if (!!this.featurePollingTimeout) {
      clearTimeout(this.featurePollingTimeout);
      this.featurePollingTimeout = null;
    }
    this.nextRefreshTime = 0;
  }

  addGoogleMapObjectsFromGeometryField(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document) {
    const geoFeatures = FeatureRenderingUtilityService.getGeometryFeaturesForDisplay(dataset, layer, document);
    if (!geoFeatures) {
      return;
    }

    for (let geo of geoFeatures) {
      this.addGoogleMapObject(context, layer, dataset, document, geo, true);
    }
  }

  addGoogleMapObjects(context: MapDrawingContext, options: FeatureRenderOptions, layer: Layer, dataset: Dataset, document: Document) {
    const allSegmentGeos = TravelerService.getSegmentsForDisplay(document);
    if (!allSegmentGeos) {
      this.addGoogleMapObjectsFromGeometryField(context, layer, dataset, document);
      return;
    }

    // -- start: get segment for display when not highlighting trip on map --
    const now = new Date().getTime();
    let geoForClusteringInfo: { index: number; timeSpan: number } = null;
    allSegmentGeos.forEach((geo, index) => {
      const timeSpan = Math.abs(TravelerService.geoPropertiesToSegmentPropertiesObject(geo?.properties)?.fromTime - now);

      if (!geoForClusteringInfo || timeSpan < geoForClusteringInfo.timeSpan) {
        geoForClusteringInfo = { timeSpan, index };
      }
    });

    if (!!geoForClusteringInfo) {
      allSegmentGeos.at(geoForClusteringInfo.index)['__shouldCluster'] = true;
    }
    // -- end: get segment for display when not highlighting trip on map --

    var allSegmentProperties = allSegmentGeos.map(geo => TravelerService.geoPropertiesToSegmentPropertiesObject(geo?.properties));

    var drawnItems = 0;

    var [mobileLocationGeo, mobileLocationDate] = TravelerService.getMobileLocationShareGeo(document);
    var mobileGeoAdded = false; // Doesn't allow the geo to be added more than once

    // If mobile location share is between two segments show that geo instead
    let hasClusterMarker = false;
    for (let geo of allSegmentGeos) {
      const [visibleNow, visibleWhen, showMobileGeo] = TravelerService.calculateTravelerFeatureVisibility(
        allSegmentProperties,
        TravelerService.geoPropertiesToSegmentPropertiesObject(geo.properties),
        mobileLocationGeo,
        mobileLocationDate
      );

      this.nextRefreshTime = !this.nextRefreshTime ? visibleWhen : Math.min(this.nextRefreshTime, visibleWhen);

      const shouldCluster = hasClusterMarker ? false : (geo['__shouldCluster'] ?? visibleNow);

      if (this.isSelected || (visibleNow && !showMobileGeo)) {
        this.addGoogleMapObject(context, layer, dataset, document, geo, shouldCluster);
        drawnItems += 1;

        if (shouldCluster) {
          hasClusterMarker = true;
        }
      }
      if ((this.isSelected || (visibleNow && !!showMobileGeo)) && !mobileGeoAdded && !!mobileLocationGeo) {
        this.addGoogleMapObject(context, layer, dataset, document, mobileLocationGeo, shouldCluster);
        drawnItems += 1;
        mobileGeoAdded = true;

        if (shouldCluster) {
          hasClusterMarker = true;
        }
      }
    }

    if (drawnItems === 0 && allSegmentGeos?.length > 0 && !this.isSelected) {
      const itemToDraw = allSegmentGeos.find(g => g['__shouldCluster'] === true);

      if (itemToDraw) {
        this.addGoogleMapObject(context, layer, dataset, document, itemToDraw, true);
        drawnItems += 1;
      }
    }

    if (!!this.nextRefreshTime && !!this.isVisible && this.nextRefreshTime !== 0) {
      var refreshLayerDelay = this.nextRefreshTime - now;

      if (refreshLayerDelay > 0) {
        if (refreshLayerDelay > ONE_DAY_MILLISEONCDS) {
          refreshLayerDelay = ONE_DAY_MILLISEONCDS;
        }
        this.startPolling(context, options, refreshLayerDelay);
      }
    }
  }

  addGoogleMapObject(
    context: MapDrawingContext,
    layer: Layer,
    dataset: Dataset,
    document: Document,
    geometry: any,
    shouldCluster: boolean
  ) {
    if (!geometry?.type) {
      return;
    }
    if (geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry, geometry?.properties, shouldCluster);
    } else if (geometry.type === GeometryTypes.polygon || geometry.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry, geometry?.properties);
    } else if (geometry.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry, geometry?.properties);
    } else if (geometry.type === GeometryTypes.feature) {
      this.addFeature(context, layer, dataset, document, geometry, geometry?.properties, shouldCluster);
    }
  }

  generateSelectedIcon(geoProperties: any, selected: boolean) {
    const properties = TravelerService.geoPropertiesToSegmentPropertiesObject(geoProperties);
    const backColor = TravelerService.getItineraryColor(properties);
    const icon = TravelerService.getItineraryIcon(properties.segmentType);
    const type = selected ? 'selected' : 'normal';
    return `${API_BASE_URL}/v1/meta/markers/sample/pin-circle_medium_${backColor}_Topo-${icon}_${type}.png`;
  }

  addPoint(
    context: MapDrawingContext,
    layer: Layer,
    dataset: Dataset,
    document: Document,
    geometry: any,
    properties: any,
    shouldCluster: boolean
  ) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const [lng = null, lat = null] = geometry?.coordinates ?? [];

    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return;
    }

    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon: {
        url: !this.isSelected ? this.markerUrls.normal : this.generateSelectedIcon(properties, false)
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: context.googleMap
    });

    const abortController = new AbortController();
    const { signal } = abortController;
    marker['__abortController'] = abortController;

    marker['__data'] = {
      id: uuidv4(),
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      properties,
      shouldCluster
    };

    const mapEventService = context.mapEvents;

    marker.addListener('mouseover', () => {
      this.onMarkerMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });
    });

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });
    });

    marker.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });
    });

    this.googleMapFeatures.markers.push(marker);

    if (shouldCluster) {
      this.clusterService.registerMarkerAdd(this.layerUId, marker, this.documentUId);
    }

    if (layer.displayOptions?.labelsVisible === true) {
      this.showLabel();
    } else {
      this.hideLabel();
    }
  }

  addPolygon(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, properties: any) {
    const coords = FeatureRenderingUtilityService.normalizePolygonCoordinates(geometry.coordinates);

    const polygon = new google.maps.Polygon({
      paths: coords,
      strokeColor: this.layerColors.borderColor,
      fillColor: this.layerColors.fillColor,
      strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
      fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    });

    polygon['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      properties: properties
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polygons.push(polygon);

    polygon.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });

    polygon.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });
  }

  addPolyline(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, properties: any) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const isValidGeometry = geometry?.coordinates?.every(c => !!c.length && c.every(x => typeof x === 'number')) ?? false;

    if (!isValidGeometry) {
      return;
    }

    const polyline = new google.maps.Polyline({
      path: geometry.coordinates?.map(c => new google.maps.LatLng(c[1], c[0])),
      strokeColor: this.layerColors.borderColor,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 2,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    });

    polyline['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      properties: properties
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polylines.push(polyline);

    polyline.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });

    polyline.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });
  }

  addFeature(
    context: MapDrawingContext,
    layer: Layer,
    dataset: Dataset,
    document: Document,
    geometry: any,
    properties: any,
    shouldCluster: boolean
  ): void {
    if (geometry?.geometry?.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry.geometry, properties, shouldCluster);
    } else if (geometry?.geometry?.type === GeometryTypes.polygon || geometry?.geometry?.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry.geometry, properties);
    } else if (geometry?.geometry?.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry.geometry, properties);
    }
  }

  updateGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document): void {
    for (let marker of this.googleMapFeatures.markers) {
      const icon = marker.getIcon() as google.maps.Icon;
      icon.url = this.isSelected ? this.generateSelectedIcon(marker['__data'].properties, false) : this.markerUrls.normal;
      marker.setIcon(icon);
    }

    if (this.googleMapFeatures.markers.length) {
      this.updateLabel(layer, this.googleMapFeatures.markers[0]);
    }

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      });
    }

    for (let circle of this.googleMapFeatures.circles) {
      circle.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      });
    }

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });
    }

    for (let rectangle of this.googleMapFeatures.rectangles) {
      rectangle.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });
    }
  }

  buildMarkerImageUrls(layer: Layer, dataset: Dataset, document: Document, cacheBuster: number): MarkerUrls {
    const badgeCount = this.getBadgeCount(dataset, document) ?? '';
    const backColor = this.layerColors.fillColor.replace('#', '');

    // if is selected calc color and icon change otherwise layer default
    const layerIconUrl = `${API_BASE_URL}/v1/meta/markers/${layer.uId}_${backColor}_${badgeCount}_${cacheBuster}.png`;
    const layerIconSelectedUrl = `${API_BASE_URL}/v1/meta/markers/${layer.uId}_${backColor}_${badgeCount}_selected_${cacheBuster}.png`;

    return {
      normal: layerIconUrl,
      selected: layerIconSelectedUrl
    };
  }

  buildDocumentHash(dataset: Dataset, layer: Layer, document: Document, isSelected: boolean, label: string): number {
    const geometryFieldName = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);

    const geometry = document[geometryFieldName] ?? '';
    const workflowStepUId = document.workflow_step_uid;
    const fillColor = document.fill_color ?? '';
    const borderColor = document.border_color ?? '';

    const stringToHash = [geometry, workflowStepUId, fillColor, borderColor, isSelected, label ?? ''].join();

    const hash = cyrb53(stringToHash);

    return hash;
  }

  buildGeometryHash(geometry: string): number {
    const hash = cyrb53(geometry);
    return hash;
  }

  getBadgeCount(dataset: Dataset, document: Document): number | null {
    if (dataset.type !== DatasetType.Incident) {
      return null;
    }

    const field = dataset.schema?.fields.find(
      f => f.dataType === FieldDataType.RelatedParent && f.systemName?.startsWith('alerts_') && document[f.systemName] !== undefined
    );
    if (!!!field) {
      return null;
    }

    const value = document[field.systemName];

    return !isNaN(value) ? value : null;
  }

  onMarkerMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setMarkerSelected();
    this.startHoverTimer();
  }

  onMarkerMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
        clearTimeout(that.timerId);
        that.timerId = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  setMarkerSelected(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      marker['__data'].zIndex = origZIndex;

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: this.generateSelectedIcon(marker['__data']?.properties, false)
      });
    }
  }

  setMarkerHovered(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      marker['__data'].zIndex = origZIndex;

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: this.generateSelectedIcon(marker['__data']?.properties, true)
      });
    }
  }

  setMarkerNormal(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const zIndex = marker['__data'].zIndex;
      marker.setZIndex(zIndex);

      marker.setIcon({
        url: this.markerUrls.normal
      });
    }
  }

  // todo update track colors
  drawTrack(document: Document, googleMap: google.maps.Map) {
    if (this.trackIsDrawn || !document.segments) {
      return;
    }

    let positions = [];
    try {
      const segments = JSON.parse(document.segments) as any[];

      segments?.forEach(seg => {
        if (!seg.geometry) {
          return;
        }
        const segmentGeo = JSON.parse(seg.geometry) as any[];
        positions.push(segmentGeo);
      });
    } catch (error) {
      console.error(error);
      return;
    }

    // build an array of points
    const points = positions.map(p => {
      const coordinates = p?.geometry?.coordinates;

      if (coordinates && coordinates.length > 1) {
        return { lat: coordinates[1], lng: coordinates[0] };
      }
      return null;
    });

    if (points.length > 0) {
      const travelerTrack = new google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: '#39ff14',
        strokeOpacity: 1.0,
        strokeWeight: 2,
        map: googleMap
      });

      this.googleMapFeatures.polylines.push(travelerTrack);
    }

    this.trackIsDrawn = true;
  }

  hideTrack(): void {
    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.setMap(null);
      polyline.unbindAll();
    }

    this.googleMapFeatures.polylines = [];
    this.trackIsDrawn = false;
  }

  startPolling(context: MapDrawingContext, options: FeatureRenderOptions, timeout: number) {
    if (!!this.featurePollingTimeout) {
      clearTimeout(this.featurePollingTimeout);
      this.featurePollingTimeout = null;
    }

    this.featurePollingTimeout = setTimeout(() => {
      this.context.clusterService.clearChanges(this.layerUId, this.documentUId);
      this.destroy();
      this.addGoogleMapObjects(context, options, this.options.layer, this.dataset, this.options.document);
      this.context.clusterService.applyChanges(this.layerUId, this.documentUId);
    }, timeout);
  }

  startHoverTimer() {
    if (!!this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    const that = this;
    this.timerId = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
      }
    }, 3000);
  }
}
